import { ChangePulseMarker, ProposedValue } from '@/features/ai-changeset/components/ProposedValue';
import type { NavigationStore } from '@/features/ai-changeset/hooks/useChangeNavigation';
import { changedRowClass, type RunPosition } from '@/features/ai-changeset/utils/changeset-view';
import { DraftTag } from '@/features/menu/components/DraftTag';
import type { ExceptionDraftRow } from '@/features/opening-hours/utils/exception-draft';
import type { useT } from '@/i18n';
import type { ExceptionValue } from '@restaurant/api-contracts';
import { Badge, ListItem } from 'framework7-react';

// Service scope → its `openingHours` i18n key (labels resolved per-locale).
const SERVICE_KEY = {
  BASE: 'openingTime',
  PICKUP: 'pickup',
  DELIVERY: 'delivery',
  RESERVATION: 'reservation',
} as const;

// 'YYYY-MM-DD' → a local Date (no UTC shift) for display formatting only.
const fmtDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const dateRange = (exception: ExceptionValue): string =>
  exception.startDate === exception.endDate
    ? fmtDate(exception.startDate)
    : `${fmtDate(exception.startDate)} – ${fmtDate(exception.endDate)}`;

export function ExceptionRow({
  key,
  row,
  run,
  store,
  t,
  onOpen,
  onRemove,
}: {
  key: string;
  row: ExceptionDraftRow;
  run: RunPosition | null;
  store: NavigationStore;
  t: ReturnType<typeof useT>;
  onOpen: (() => void) | null;
  onRemove: (() => void) | null;
}) {
  const value = row.value;
  const before = row.before;
  const removed = row.status === 'removed';
  const scopeLabel = (exception: ExceptionValue): string => {
    const services = Object.keys(SERVICE_KEY);
    if (exception.services.length >= services.length) return t.openingHours.allServices;
    return exception.services.map((service) => t.openingHours[SERVICE_KEY[service]]).join(', ');
  };
  const scopeAndHours = (exception: ExceptionValue): string => {
    const scope = scopeLabel(exception);
    if (exception.type === 'CLOSED') return `${scope} · ${t.openingHours.closedAllDay}`;
    const windows = (exception.shifts ?? [])
      .map((shift) => `${shift.open}–${shift.close}`)
      .join(', ');
    return `${scope} · ${windows || t.openingHours.specialHours}`;
  };
  const range = dateRange(value);
  const beforeRange = before ? dateRange(before) : range;
  const reasonChanged = !!before && before.reason !== value.reason;
  const rangeChanged = !!before && beforeRange !== range;
  const scope = scopeAndHours(value);
  const beforeScope = before ? scopeAndHours(before) : scope;
  const badge = value.type === 'CLOSED' ? t.openingHours.closedBadge : t.openingHours.specialBadge;
  const beforeBadge = before
    ? before.type === 'CLOSED'
      ? t.openingHours.closedBadge
      : t.openingHours.specialBadge
    : badge;
  const draftClass = row.status === 'new' ? 'ai-changed-text' : removed ? 'quick-edit-old' : '';
  const title = value.reason || range;
  const beforeTitle = before?.reason || beforeRange;
  const titleChanged = reasonChanged || (!value.reason && !before?.reason && rangeChanged);
  const subtitle = value.reason ? range : undefined;
  const beforeSubtitle = before ? beforeRange : undefined;
  const interactive = onOpen !== null;
  const classes = [
    'menu-item-row',
    removed && 'menu-item-row--muted menu-item-row--removed',
    changedRowClass(run),
  ].filter(Boolean);

  return (
    <ListItem
      key={key}
      link={interactive ? '#' : undefined}
      swipeout={interactive}
      className={classes.join(' ')}
      data-quick-edit-target={row.id}
      data-view-row={row.id}
      onClick={onOpen ?? undefined}
    >
      {row.status !== null && (
        <ChangePulseMarker
          slot="root-start"
          store={store}
          id={row.id}
          testId={`exception-pulse-${row.id}`}
        />
      )}
      <span slot="title" className={draftClass}>
        {titleChanged ? <ProposedValue start before={beforeTitle} after={title} /> : title}
        {row.status === 'new' && <DraftTag status="new" testId={`exception-tag-${row.id}`} />}
      </span>
      {subtitle && (
        <span slot="subtitle" className={draftClass}>
          {rangeChanged && beforeSubtitle ? (
            <ProposedValue start before={beforeSubtitle} after={subtitle} />
          ) : (
            subtitle
          )}
        </span>
      )}
      <span slot="text" className={draftClass}>
        {beforeScope !== scope ? <ProposedValue start before={beforeScope} after={scope} /> : scope}
      </span>
      <div slot="after">
        <Badge color={value.type === 'CLOSED' ? 'red' : 'blue'}>
          {beforeBadge !== badge ? (
            <ProposedValue before={beforeBadge} after={badge} />
          ) : (
            <span className={draftClass}>{badge}</span>
          )}
        </Badge>
      </div>
      {interactive && (
        <div slot="swipeout-actions-right">
          <button type="button" className="swipeout-close color-blue" onClick={onOpen ?? undefined}>
            {t.edit}
          </button>
          <button
            type="button"
            className="swipeout-close color-red"
            onClick={onRemove ?? undefined}
          >
            {t.delete}
          </button>
        </div>
      )}
    </ListItem>
  );
}
